// src/pages/Ueber.jsx
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Container from "../components/ui/Container";
import Badge from "../components/ui/Badge";
import Section from "../components/ui/Section";
import Button from "../components/ui/Button";
import GoogleReviewsSection from "../sections/GoogleReviews";
import FAQ from "../components/FAQ";
import { FAQ_ALLGEMEIN } from "../data/faq";
import {
    Phone, Mail, ChevronDown, Shield, Target, Award, TreePine
} from "lucide-react";

const PERSON = {
    name: "Helmut Tuscher",
    title: "Jäger & Entwickler der Betonrohrfalle",
    intro:
        "Seit über 40 Jahren in der Praxis und seit 20 Jahren im direkten Austausch mit Revierinhabern, Hegeringen und Behörden.",
};

const STATS = [
    { label: "Jahre Erfahrung", value: "40+", icon: Award },
    { label: "aktive Fallen im Feld", value: "300+", icon: Target },
    { label: "Zufriedene Kunden", value: "100+", icon: Shield },
];

const GALLERY = [
    { src: "/images/ueber/galerie-1.webp", alt: "Betonrohrfalle im Revier" },
    { src: "/images/ueber/galerie-2.webp", alt: "Aufbau einer Falle" },
    { src: "/images/ueber/galerie-3.webp", alt: "Praxis im Wald" },
];

const VALUES = [
    {
        icon: Shield,
        title: "Tierschutzkonform",
        desc: "Alle Fallen entsprechen den gesetzlichen Vorgaben und gewährleisten einen tierschutzgerechten Lebendfang.",
    },
    {
        icon: TreePine,
        title: "Aus der Praxis",
        desc: "Jedes Produkt wird unter realen Bedingungen im Revier getestet und stetig verbessert.",
    },
    {
        icon: Award,
        title: "Persönliche Beratung",
        desc: "Direkter Kontakt zum Hersteller — ich berate Sie bei Fallenauswahl, Aufbau und Standortwahl.",
    },
];

const FAQS = [
    {
        q: "Warum diese Falle?",
        a: "Das Hans-Hubner System ermöglicht einen mühelosen und unkomplizierten Aufbau der Falle. Ich stehe Ihnen stets zur Verfügung, um Sie mit kompetentem Rat und meiner langjährigen Erfahrung zu unterstützen.",
    },
    {
        q: "Was ist eine Betonrohrfalle?",
        a: "Die Betonrohrfalle, bestehend aus einem Betonrohr, wird zur lebendigen Fangmethode von Raubwild verwendet. Ihren Namen verdankt sie den langen Betonrohren, in denen das Raubwild gefangen wird. Diese Fallen bleiben in der Regel über einen längeren Zeitraum am gleichen Fangplatz im Einsatz.",
    },
    {
        q: "Wie viele Fallen im Revier?",
        a: "Um herauszufinden, welche und wie viele Fallen für das betreffende Revier notwendig sind, ist es zunächst erforderlich, passende Fangplätze zu identifizieren. Als grobe Richtlinie gilt, dass pro 100 Hektar Revierfläche ein bis zwei Fallen für den Fuchsfang, zwei bis vier für den Waschbärfang und vier bis sechs Marderfallen eingeplant werden sollten.",
    },
    {
        q: "Entstehen Wartungsarbeiten?",
        a: "Diese Falle kann über viele Jahre hinweg problemlos verwendet werden, ohne dass dabei Wartung oder Reparatur erforderlich sind.",
    },
    {
        q: "Warum Fallenjagd?",
        a: "Angesichts rückläufiger Zahlen bei erlegtem Niederwild und dem schwindenden Bestand von Bodenbrütern sowie anderen seltenen Tierarten ist die Fallenjagd erforderlich, um nachtaktive Raubtiere wirksam und unter Berücksichtigung des Tierschutzes zu regulieren.",
    },
];

export default function Ueber() {
    useEffect(() => {
        document.title = "Über Helmut Tuscher | 40 Jahre Fallenjagd-Erfahrung";
        
        const metaDesc = document.querySelector('meta[name="description"]');
        if (metaDesc) {
            metaDesc.setAttribute("content", "Lernen Sie Helmut Tuscher kennen: 40+ Jahre Praxiserfahrung in der Raubwildbejagung. Entwickler der bewährten Betonrohrfalle. Persönliche Beratung vom Experten.");
        }
        
        const personSchema = {
            "@context": "https://schema.org",
            "@type": "Person",
            "name": "Helmut Tuscher",
            "jobTitle": "Jäger & Fallenentwickler",
            "description": "Über 40 Jahre Erfahrung in der Fallenjagd und Entwicklung von Betonrohrfallen",
            "knowsAbout": ["Fallenjagd", "Betonrohrfallen", "Raubwildbejagung", "Lebendfang"],
            "worksFor": {
                "@type": "Organization",
                "name": "Tuscher Helmut Betonrohrfallen"
            }
        };
        
        const script = document.createElement("script");
        script.id = "person-schema";
        script.type = "application/ld+json";
        script.textContent = JSON.stringify(personSchema);
        
        const existing = document.getElementById("person-schema");
        if (existing) existing.remove();
        document.head.appendChild(script);
        
        return () => {
            const el = document.getElementById("person-schema");
            if (el) el.remove();
        };
    }, []);

    return (
        <div className="relative overflow-hidden min-h-screen">
            {/* Hintergrund Dekoration */}
            <div className="pointer-events-none absolute inset-0 -z-10 overflow-hidden">
                <div className="absolute -top-[10%] -left-[10%] h-[40%] w-[40%] rounded-full bg-brand-500/5 blur-[120px] dark:bg-brand-500/10" />
                <div className="absolute top-[30%] -right-[10%] h-[40%] w-[40%] rounded-full bg-blue-500/5 blur-[120px] dark:bg-blue-500/5" />
                <div className="absolute -bottom-[10%] left-[20%] h-[40%] w-[40%] rounded-full bg-brand-500/5 blur-[120px] dark:bg-brand-500/10" />
                <div 
                    className="absolute inset-0 opacity-[0.12] dark:opacity-[0.04]" 
                    style={{ 
                        backgroundImage: 'radial-gradient(#64748b 0.5px, transparent 0.5px)', 
                        backgroundSize: '24px 24px' 
                    }} 
                />
            </div>

            <Container className="relative py-14 sm:py-20 lg:py-24 space-y-20 lg:space-y-32">
                
                {/* HERO — Full-width mit Galerie-Bild als Background */}
                <section className="relative rounded-[3rem] overflow-hidden min-h-[500px] lg:min-h-[600px] flex items-end">
                    {/* Hintergrundbild */}
                    <img 
                        src="/images/ueber/galerie-1.webp" 
                        alt="Helmut Tuscher im Revier" 
                        className="absolute inset-0 h-full w-full object-cover"
                    />
                    {/* Overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/60 to-transparent" />
                    
                    <div className="relative z-10 p-8 sm:p-12 lg:p-16 w-full">
                        <Badge className="mb-4 bg-white/10 text-white border-white/20 uppercase tracking-[0.2em] text-[10px] backdrop-blur-sm">
                            Meine Erfahrung & Leidenschaft
                        </Badge>
                        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-black tracking-tight text-white uppercase leading-[1.1]">
                            Tuscher <span className="text-brand-400">Helmut</span>
                        </h1>
                        <p className="mt-4 text-lg sm:text-xl text-slate-300 leading-relaxed max-w-2xl">
                            {PERSON.intro}
                        </p>
                        
                        {/* Stats inline */}
                        <div className="mt-8 flex flex-wrap gap-3">
                            {STATS.map((s) => (
                                <div key={s.label} className="flex items-center gap-3 rounded-2xl bg-white/10 backdrop-blur-sm border border-white/10 px-5 py-3">
                                    <s.icon className="h-5 w-5 text-brand-400 shrink-0" />
                                    <div>
                                        <span className="text-xl font-black text-white tabular-nums">{s.value}</span>
                                        <span className="ml-2 text-sm text-slate-400">{s.label}</span>
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div className="mt-8 flex flex-wrap gap-4">
                            <Button as={Link} to="/shop" variant="solid" className="px-8 py-4 shadow-xl shadow-brand-500/20">
                                Produkte ansehen
                            </Button>
                            <Button as={Link} to="/kontakt" variant="outline" className="px-8 py-4 !border-white/40 !text-white !bg-white/10 backdrop-blur-sm hover:!bg-white/20">
                                Beratung anfragen
                            </Button>
                        </div>
                    </div>
                </section>

                {/* WERTE / USPs */}
                <section>
                    <div className="text-center mb-12">
                        <Badge className="mb-4 bg-brand-50 text-brand-600 dark:bg-brand-900/20 border-brand-100/50">
                            Warum Tuscher Helmut
                        </Badge>
                        <h2 className="text-3xl sm:text-4xl font-heading font-black tracking-tight text-slate-900 dark:text-white">
                            Qualität aus Überzeugung
                        </h2>
                    </div>
                    <div className="grid sm:grid-cols-3 gap-6">
                        {VALUES.map((v, i) => (
                            <div 
                                key={i} 
                                className="group relative rounded-3xl border border-slate-200/60 dark:border-slate-800 bg-white/80 dark:bg-slate-900/80 backdrop-blur-sm p-8 transition-all duration-500 hover:shadow-2xl hover:shadow-brand-500/10 hover:-translate-y-2 hover:border-brand-200 dark:hover:border-brand-800"
                            >
                                <div className="h-14 w-14 rounded-2xl bg-brand-50 dark:bg-brand-900/30 flex items-center justify-center mb-6 group-hover:bg-brand-600 group-hover:text-white transition-all duration-300 text-brand-600">
                                    <v.icon className="h-7 w-7" />
                                </div>
                                <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-2">{v.title}</h3>
                                <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed">{v.desc}</p>
                            </div>
                        ))}
                    </div>
                </section>

                {/* GALERIE */}
                <section>
                    <div className="text-center mb-12">
                        <Badge className="mb-4 bg-brand-50 text-brand-600 dark:bg-brand-900/20 border-brand-100/50">
                            Einblicke
                        </Badge>
                        <h2 className="text-3xl sm:text-4xl font-heading font-black tracking-tight text-slate-900 dark:text-white">
                            Aus dem Revier
                        </h2>
                        <p className="mt-3 text-slate-600 dark:text-slate-400 max-w-lg mx-auto">
                            Impressionen aus der Praxis — von der Fertigung bis zum Einsatz im Feld.
                        </p>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        {GALLERY.map((img, i) => (
                            <div 
                                key={i} 
                                className={`group relative overflow-hidden rounded-3xl border border-slate-200/60 dark:border-slate-800 ${
                                    i === 0 ? "sm:row-span-2 sm:h-full" : "aspect-[4/3]"
                                }`}
                            >
                                <img 
                                    src={img.src} 
                                    alt={img.alt} 
                                    className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110"
                                    loading="lazy"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                                <div className="absolute bottom-0 left-0 right-0 p-4 translate-y-full group-hover:translate-y-0 transition-transform duration-500">
                                    <span className="text-sm font-medium text-white">{img.alt}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </section>

                {/* REVIEWS */}
                <GoogleReviewsSection
                    title="Was meine Kunden sagen"
                    subtitle="Echt. Unverändert. Von Google."
                    ctaTo="https://g.page/r/CcB_B2dngBtxEBM/review"
                />

                {/* FAQ zur Fallenjagd */}
                <Section eyebrow="Wissen" title="Häufige Fragen zur Fallenjagd">
                    <div className="max-w-3xl mx-auto rounded-[2.5rem] border border-slate-200/60 dark:border-slate-800 bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl shadow-xl overflow-hidden divide-y divide-slate-100 dark:divide-slate-800">
                        {FAQS.map((f, idx) => (
                            <FaqItem key={idx} q={f.q} a={f.a} defaultOpen={idx === 0} />
                        ))}
                    </div>
                </Section>

                {/* FAQ zu Bestellung & Versand */}
                <FAQ 
                    items={FAQ_ALLGEMEIN}
                    title="Bestellung, Versand & Service"
                    subtitle="Alles rund um Ihren Einkauf"
                    schemaId="faq-allgemein"
                />

                {/* KONTAKT-CTA */}
                <section className="relative overflow-hidden rounded-[3rem] bg-slate-950 p-8 sm:p-12 lg:p-16 text-white group shadow-2xl">
                    <div className="absolute -top-24 -right-24 h-64 w-64 rounded-full bg-brand-600/20 blur-[100px] transition-transform duration-1000 group-hover:scale-125" />
                    <div className="absolute -bottom-16 -left-16 h-48 w-48 rounded-full bg-blue-600/10 blur-[80px]" />
                    
                    <div className="relative z-10 max-w-3xl">
                        <h3 className="text-3xl sm:text-4xl font-heading font-black tracking-tight uppercase leading-tight">
                            Fragen oder Beratung gewünscht?
                        </h3>
                        <p className="mt-4 text-slate-400 text-lg">
                            Melde dich einfach – ich helfe dir gerne weiter.
                        </p>
                        <div className="mt-10 flex flex-wrap gap-4">
                            <a href="tel:+491634194552" className="flex items-center gap-3 rounded-2xl bg-white px-6 py-4 text-slate-950 font-black hover:bg-slate-100 transition-all active:scale-95 shadow-xl">
                                <Phone className="h-5 w-5" /> 0163 4194552
                            </a>
                            <a href="mailto:info@tuscherhelmut-betonrohrfallen.de" className="flex items-center gap-3 rounded-2xl border border-white/20 bg-white/5 backdrop-blur-sm px-6 py-4 font-bold hover:bg-white/10 transition-all active:scale-95">
                                <Mail className="h-5 w-5" /> E-Mail senden
                            </a>
                        </div>
                    </div>
                </section>
            </Container>
        </div>
    );
}

function FaqItem({ q, a, defaultOpen = false }) {
    const [open, setOpen] = useState(defaultOpen);
    return (
        <div className="transition-all duration-300">
            <button
                onClick={() => setOpen((o) => !o)}
                className="w-full flex items-center justify-between gap-4 px-6 sm:px-8 py-6 text-left hover:bg-slate-50 dark:hover:bg-slate-800/30 transition-colors"
                aria-expanded={open}
            >
                <span className="font-bold text-slate-900 dark:text-slate-100">{q}</span>
                <div className={`h-8 w-8 rounded-full border border-slate-200 dark:border-slate-700 flex items-center justify-center transition-all shrink-0 ${open ? 'bg-brand-600 border-brand-600 text-white rotate-180' : 'text-slate-400'}`}>
                    <ChevronDown className="h-4 w-4" />
                </div>
            </button>
            <div className={`overflow-hidden transition-all duration-500 ease-in-out ${open ? "max-h-[500px] opacity-100" : "max-h-0 opacity-0"}`}>
                <div className="px-6 sm:px-8 pb-8 text-slate-600 dark:text-slate-400 leading-relaxed text-sm md:text-base">
                    {a}
                </div>
            </div>
        </div>
    );
}
